/**
 * The Projects module's route table (CLAUDE.md §8.13).
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `PROJECTS_ROUTES` in. Paths, permissions and
 * the components that implement them are therefore decided in one place by
 * whoever owns the module.
 *
 * Three shapes here are load-bearing:
 *
 *  - Every `:id` is constrained to `\d+`, so `/projects/timesheets` can never be
 *    parsed as project number "timesheets". The timesheet screens deliberately
 *    live **under** `/projects/` rather than at a top-level `/timesheets`,
 *    because a timesheet is only ever about projects and a person looking for
 *    one looks in the project section — the API's own path (`/api/v1/timesheets`)
 *    is a resource path, not a navigation one.
 *  - A project's four surfaces — overview, Gantt, profitability, billing — are
 *    four routes rather than four tabs in one, because each is a different
 *    question with a different permission, and each is worth its own URL to send
 *    somebody. `ProjectTabs.vue` renders the strip; the router owns the truth.
 *  - The profitability and billing routes carry `reportRead` and `billingManage`
 *    respectively, matching the server exactly. A user who may see a project but
 *    not its margins does not get a route that 403s.
 */

import type { RouteRecordRaw } from 'vue-router'

import {
  PROJECTS_C11_PERMISSIONS,
  PROJECTS_PERMISSIONS,
} from '@/modules/projects/permissions'

export const PROJECTS_ROUTES: RouteRecordRaw[] = [
  {
    path: '/projects',
    name: 'projects-list',
    component: () => import('@/modules/projects/ProjectsListView.vue'),
    meta: {
      titleKey: 'projects.routeTitles.projects',
      permission: PROJECTS_PERMISSIONS.projectRead,
    },
  },

  /* Timesheets come before `/projects/:id` only for the reader's benefit — the
     `\d+` constraint is what actually keeps them apart. */
  {
    path: '/projects/timesheets',
    name: 'projects-timesheets',
    component: () => import('@/modules/projects/TimesheetsListView.vue'),
    meta: {
      titleKey: 'projects.routeTitles.timesheets',
      permission: PROJECTS_PERMISSIONS.timesheetSelfService,
    },
  },
  {
    path: '/projects/timesheets/:id(\\d+)',
    name: 'projects-timesheet',
    component: () => import('@/modules/projects/TimesheetView.vue'),
    meta: {
      titleKey: 'projects.routeTitles.timesheet',
      permission: PROJECTS_PERMISSIONS.timesheetSelfService,
    },
  },

  {
    path: '/projects/settings',
    name: 'projects-settings',
    component: () => import('@/modules/projects/SettingsView.vue'),
    meta: {
      titleKey: 'projects.routeTitles.settings',
      permission: PROJECTS_PERMISSIONS.settingsManage,
    },
  },

  {
    path: '/projects/:id(\\d+)',
    name: 'projects-project',
    component: () => import('@/modules/projects/ProjectView.vue'),
    meta: {
      titleKey: 'projects.routeTitles.project',
      permission: PROJECTS_PERMISSIONS.projectRead,
    },
  },
  {
    path: '/projects/:id(\\d+)/gantt',
    name: 'projects-gantt',
    component: () => import('@/modules/projects/GanttView.vue'),
    meta: { titleKey: 'projects.routeTitles.gantt', permission: PROJECTS_PERMISSIONS.taskRead },
  },
  {
    path: '/projects/:id(\\d+)/profitability',
    name: 'projects-profitability',
    component: () => import('@/modules/projects/ProfitabilityView.vue'),
    meta: {
      titleKey: 'projects.routeTitles.profitability',
      permission: PROJECTS_PERMISSIONS.reportRead,
    },
  },
  {
    path: '/projects/:id(\\d+)/billing',
    name: 'projects-billing',
    component: () => import('@/modules/projects/BillingView.vue'),
    meta: {
      titleKey: 'projects.routeTitles.billing',
      permission: PROJECTS_PERMISSIONS.billingManage,
    },
  },

  /* C11's contract, schedule and resource surfaces.

     The registers are tenant-level because a commercial manager reads them
     ACROSS a portfolio — "what retention falls due this quarter" is not a
     question about one project — while the four per-project surfaces are
     `/projects/:id/...` for the same reason the existing four are: each is a
     different question with a different permission and each is worth its own
     URL to send somebody.

     The static paths cannot be swallowed by `/projects/:id`, because that
     parameter is constrained to `\d+`. */
  {
    path: '/projects/wip-runs',
    name: 'projects-wip-runs',
    component: () => import('@/modules/projects/c11/WipRunsView.vue'),
    meta: {
      titleKey: 'projects.c11.routeTitles.wipRuns',
      permission: PROJECTS_C11_PERMISSIONS.wipRead,
    },
  },
  {
    path: '/projects/retentions',
    name: 'projects-retentions',
    component: () => import('@/modules/projects/c11/RetentionsView.vue'),
    meta: {
      titleKey: 'projects.c11.routeTitles.retentions',
      permission: PROJECTS_C11_PERMISSIONS.retentionRead,
    },
  },
  {
    // Every advance route — list and get included — declares
    // `projects.advance.manage` server-side, so the screen does too. A
    // read-only advance permission does not exist to gate it on.
    path: '/projects/advances',
    name: 'projects-advances',
    component: () => import('@/modules/projects/c11/AdvancesView.vue'),
    meta: {
      titleKey: 'projects.c11.routeTitles.advances',
      permission: PROJECTS_C11_PERMISSIONS.advanceManage,
    },
  },
  {
    path: '/projects/allocations',
    name: 'projects-allocations',
    component: () => import('@/modules/projects/c11/AllocationBoardView.vue'),
    meta: {
      titleKey: 'projects.c11.routeTitles.allocations',
      permission: PROJECTS_C11_PERMISSIONS.allocationRead,
    },
  },
  {
    path: '/projects/resources',
    name: 'projects-resources',
    component: () => import('@/modules/projects/c11/ResourcesView.vue'),
    meta: {
      titleKey: 'projects.c11.routeTitles.resources',
      permission: PROJECTS_C11_PERMISSIONS.resourceRead,
    },
  },
  {
    path: '/projects/variations',
    name: 'projects-variations',
    component: () => import('@/modules/projects/c11/VariationsView.vue'),
    meta: {
      titleKey: 'projects.c11.routeTitles.variations',
      permission: PROJECTS_C11_PERMISSIONS.variationRead,
    },
  },
  {
    // Risks and subcontracts are read under `projects.project.read` server-side
    // (`projects.risks.list`, `projects.subcontracts.list`); only writing them
    // needs the manage grant.
    path: '/projects/risks',
    name: 'projects-risks',
    component: () => import('@/modules/projects/c11/RisksView.vue'),
    meta: {
      titleKey: 'projects.c11.routeTitles.risks',
      permission: PROJECTS_PERMISSIONS.projectRead,
    },
  },
  {
    path: '/projects/subcontracts',
    name: 'projects-subcontracts',
    component: () => import('@/modules/projects/c11/SubcontractsView.vue'),
    meta: {
      titleKey: 'projects.c11.routeTitles.subcontracts',
      permission: PROJECTS_PERMISSIONS.projectRead,
    },
  },
  {
    path: '/projects/contract-settings',
    name: 'projects-contract-settings',
    component: () => import('@/modules/projects/c11/ContractSettingsView.vue'),
    meta: {
      titleKey: 'projects.c11.routeTitles.contractSettings',
      permission: PROJECTS_PERMISSIONS.settingsManage,
    },
  },

  {
    path: '/projects/:id(\\d+)/baselines',
    name: 'projects-baselines',
    component: () => import('@/modules/projects/c11/BaselinesView.vue'),
    meta: {
      titleKey: 'projects.c11.routeTitles.baselines',
      permission: PROJECTS_C11_PERMISSIONS.baselineRead,
    },
  },
  {
    path: '/projects/:id(\\d+)/schedule',
    name: 'projects-schedule',
    component: () => import('@/modules/projects/c11/ScheduleView.vue'),
    meta: {
      titleKey: 'projects.c11.routeTitles.schedule',
      permission: PROJECTS_PERMISSIONS.taskRead,
    },
  },
  {
    path: '/projects/:id(\\d+)/evm',
    name: 'projects-evm',
    component: () => import('@/modules/projects/c11/EvmView.vue'),
    meta: {
      titleKey: 'projects.c11.routeTitles.evm',
      permission: PROJECTS_PERMISSIONS.reportRead,
    },
  },
  {
    path: '/projects/:id(\\d+)/contract',
    name: 'projects-contract',
    component: () => import('@/modules/projects/c11/ContractView.vue'),
    meta: {
      titleKey: 'projects.c11.routeTitles.contract',
      permission: PROJECTS_C11_PERMISSIONS.chargeabilityRead,
    },
  },
]

/* ------------------------------------------------------------ own screens */

export const PROJECTS_PATHS = {
  projects: '/projects',
  project: (id: number) => `/projects/${id}`,
  gantt: (id: number) => `/projects/${id}/gantt`,
  profitability: (id: number) => `/projects/${id}/profitability`,
  billing: (id: number) => `/projects/${id}/billing`,
  timesheets: '/projects/timesheets',
  timesheet: (id: number) => `/projects/timesheets/${id}`,
  settings: '/projects/settings',

  /* C11 (§5.5): the registers a commercial manager reads across a portfolio,
     and the four per-project contract surfaces. */
  wipRuns: '/projects/wip-runs',
  retentions: '/projects/retentions',
  advances: '/projects/advances',
  allocations: '/projects/allocations',
  resources: '/projects/resources',
  variations: '/projects/variations',
  risks: '/projects/risks',
  subcontracts: '/projects/subcontracts',
  contractSettings: '/projects/contract-settings',
  baselines: (id: number) => `/projects/${id}/baselines`,
  schedule: (id: number) => `/projects/${id}/schedule`,
  evm: (id: number) => `/projects/${id}/evm`,
  contract: (id: number) => `/projects/${id}/contract`,
} as const

/**
 * The approved timesheets behind a project's `actualTimesheet` figure.
 *
 * This is the one profitability drill-down that lands on a genuinely filtered
 * list: `GET /timesheets` takes both `projectId` and `status`, so the rows the
 * link opens are exactly the rows the figure summed.
 */
export function approvedTimesheetsFor(projectId: number): string {
  return `${PROJECTS_PATHS.timesheets}?projectId=${projectId}&status=approved`
}

/** One employee's timesheets — the hop from an approval back to their history. */
export function timesheetsForEmployee(employeeUserId: number): string {
  return `${PROJECTS_PATHS.timesheets}?employeeUserId=${employeeUserId}`
}

/* -------------------------------------------------------- links elsewhere */

/** The AR invoice a billing raised. `internal/ar` owns this URL. */
export function arInvoiceRoute(invoiceId: number): string {
  return `/ar/invoices/${invoiceId}`
}

/** The journal entry a timesheet's costing posted. `internal/gl` owns this URL. */
export function journalEntryRoute(entryId: number): string {
  return `/gl/journal-entries/${entryId}`
}

/**
 * Journal entries this module posted, for the company in question.
 *
 * `GET /journal-entries` filters by `source`, and `internal/gl` registers
 * `projects` as a source of its own (migration 000021), so this is the whole
 * ledger side of project costing — every WIP posting and nothing else.
 */
export function projectJournalsRoute(companyId: number): string {
  return `/gl/journal-entries?source=projects&companyId=${companyId}`
}

/**
 * Purchase orders, as close as this app can get to "the ones behind the
 * commitment figure".
 *
 * `GET /purchase-orders` has **no `projectId` filter** — commitment is computed
 * server-side from `purchase_order_lines.project_id`, a column no list endpoint
 * exposes as a filter. So this link opens the company's open orders and the
 * screen says, in words, that the list is wider than the figure. Sending
 * somebody to a list that silently contains other projects' orders without
 * saying so would be worse than not linking at all.
 */
export function openPurchaseOrdersRoute(companyId: number): string {
  return `/procurement/purchase-orders?companyId=${companyId}&status=issued`
}

/** Posted AP invoices for the company — the same caveat as commitment applies. */
export function postedApInvoicesRoute(companyId: number): string {
  return `/ap/invoices?companyId=${companyId}&status=posted`
}

/** The `internal/ar` customer a project bills. AR owns this URL. */
export function customerRoute(customerId: number): string {
  return `/ar/customers/${customerId}`
}
